import { ChevronLeft } from "lucide-react";

const sectionClass = "mb-8";
const headingClass = "font-montserrat font-bold text-xl text-white border-l-2 border-[#E0A458] pl-3 mb-3";
const cardClass = "rounded-xl border border-[#E0A458]/20 bg-[#0D1B2A]/60 p-5 space-y-3";
const bodyClass = "text-slate-300 text-sm leading-relaxed";
const listClass = "list-disc list-inside space-y-1.5 text-slate-300 text-sm leading-relaxed";

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-transparent text-white">
      <div className="max-w-3xl mx-auto px-4 py-12">

        <a href="/" className="inline-flex items-center text-[#E0A458] hover:text-[#f0b467] text-sm mb-8 transition-colors">
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back to Home
        </a>

        <div className="rounded-xl border border-[#E0A458]/45 bg-gradient-to-r from-[#0D1B2A] to-[#1B263B] px-6 py-6 shadow-lg shadow-black/25 mb-8">
          <h1 className="font-montserrat font-bold text-3xl text-white">Terms of Service</h1>
          <p className="text-slate-400 text-sm mt-1">Effective Date: May 18, 2025</p>
        </div>

        <div className={sectionClass}>
          <h2 className={headingClass}>1. Acceptance of Terms</h2>
          <div className={cardClass}>
            <p className={bodyClass}>
              By creating an account or using LegacyLoop at mylegacyloop.com, you agree to be bound by these Terms of Service ("Terms") and our Privacy Policy. If you do not agree to these Terms, do not use the service.
            </p>
            <p className={bodyClass}>
              You must be at least 18 years old to create an account. By agreeing to these Terms, you represent that you are 18 or older and have the authority to bind yourself and any minor athletes whose information you add to your account.
            </p>
          </div>
        </div>

        <div className={sectionClass}>
          <h2 className={headingClass}>2. Description of Service</h2>
          <div className={cardClass}>
            <p className={bodyClass}>
              LegacyLoop is a private media vault and memory platform for youth sports families. The service allows you to upload and organize photos and videos, maintain a digital journal, manage athlete recruiting information, share content with family members, and preserve athletic milestones and achievements.
            </p>
          </div>
        </div>

        <div className={sectionClass}>
          <h2 className={headingClass}>3. Account Registration</h2>
          <div className={cardClass}>
            <p className={bodyClass}>
              You are responsible for maintaining the security of your account credentials. Do not share your password. You are responsible for all activity that occurs under your account. Notify us immediately at support@mylegacyloop.com if you suspect unauthorized access.
            </p>
            <p className={bodyClass}>
              You may not create accounts on behalf of others without their permission, use false information to register, or create more than one account per person.
            </p>
          </div>
        </div>

        <div className={sectionClass}>
          <h2 className={headingClass}>4. Subscription Plans and Billing</h2>
          <div className={cardClass}>
            <p className={bodyClass}>LegacyLoop offers the following plans:</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-slate-300 border-collapse mt-2">
                <thead>
                  <tr className="border-b border-[#1B263B]">
                    <th className="text-left py-2 pr-4 text-white font-medium">Plan</th>
                    <th className="text-left py-2 pr-4 text-white font-medium">Price</th>
                    <th className="text-left py-2 pr-4 text-white font-medium">Athletes</th>
                    <th className="text-left py-2 text-white font-medium">Storage</th>
                  </tr>
                </thead>
                <tbody className="space-y-1">
                  <tr className="border-b border-[#1B263B]/50">
                    <td className="py-2 pr-4">Free</td>
                    <td className="py-2 pr-4">$0</td>
                    <td className="py-2 pr-4">1</td>
                    <td className="py-2">500 MB</td>
                  </tr>
                  <tr className="border-b border-[#1B263B]/50">
                    <td className="py-2 pr-4">Loop Family</td>
                    <td className="py-2 pr-4">$99/yr or $12.99/mo</td>
                    <td className="py-2 pr-4">2</td>
                    <td className="py-2">1 TB</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4">Loop Recruit</td>
                    <td className="py-2 pr-4">$199/yr</td>
                    <td className="py-2 pr-4">2</td>
                    <td className="py-2">2 TB</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className={bodyClass}>
              Paid subscriptions are billed annually or monthly, depending on the plan you choose. Your plan renews automatically at the end of each billing period until cancelled. All prices are in USD. We reserve the right to update pricing with 30 days' notice to existing subscribers.
            </p>
            <p className={bodyClass}>
              Payment is processed by Stripe. By subscribing, you authorize Stripe to charge your payment method on a recurring basis for your selected billing period.
            </p>
          </div>
        </div>

        <div className={sectionClass}>
          <h2 className={headingClass}>5. Free Plan Limitations</h2>
          <div className={cardClass}>
            <p className={bodyClass}>
              The Free plan includes core features with the following limitations:
            </p>
            <ul className={listClass}>
              <li>1 athlete profile</li>
              <li>500 MB total storage</li>
              <li>Maximum 50 MB per individual file upload</li>
              <li>Media items are displayed with a LegacyLoop watermark</li>
            </ul>
            <p className={bodyClass}>
              Upgrading to a paid plan removes watermarks, increases storage, and unlocks additional athletes.
            </p>
          </div>
        </div>

        <div className={sectionClass}>
          <h2 className={headingClass}>6. Cancellation and Refunds</h2>
          <div className={cardClass}>
            <p className={bodyClass}>
              You may cancel your subscription at any time from the Subscription page in your account or by contacting support@mylegacyloop.com. Cancellation takes effect at the end of the current billing period. You will retain access to paid features until that date.
            </p>
            <p className={bodyClass}>
              Subscription fees are non-refundable except where required by applicable law. If you believe you were charged in error, contact us within 30 days of the charge and we will review your request.
            </p>
            <p className={bodyClass}>
              After cancellation, your account and all uploaded content will be retained for 30 days. You are responsible for downloading any media you wish to keep before that 30-day window closes. After 30 days, your account and data are permanently deleted.
            </p>
          </div>
        </div>

        <div className={sectionClass}>
          <h2 className={headingClass}>7. Storage Limits and Fair Use</h2>
          <div className={cardClass}>
            <p className={bodyClass}>
              Your account is subject to the storage limit for your plan. If you exceed your storage limit, you will be unable to upload new files until you free up space or upgrade your plan. We will make reasonable efforts to notify you when you approach your limit.
            </p>
            <p className={bodyClass}>
              LegacyLoop is intended for personal, family use to document youth athletic journeys. Commercial use, bulk content hosting for third parties, and any use that places unreasonable demand on our infrastructure are prohibited.
            </p>
          </div>
        </div>

        <div className={sectionClass}>
          <h2 className={headingClass}>8. User Content and License</h2>
          <div className={cardClass}>
            <p className={bodyClass}>
              You retain full ownership of all content you upload to LegacyLoop, including photos, videos, journal entries, and athlete data. By uploading content, you grant LegacyLoop a limited, non-exclusive, royalty-free license to store, display, and transmit your content solely for the purpose of providing the service to you and the family members you authorize.
            </p>
            <p className={bodyClass}>
              We will never use your content in marketing, advertising, or publicly-facing materials without your explicit written consent.
            </p>
            <p className={bodyClass}>
              You are responsible for ensuring you have the right to upload any content you add to LegacyLoop. Do not upload content that infringes the copyright, privacy, or other rights of third parties.
            </p>
          </div>
        </div>

        <div className={sectionClass}>
          <h2 className={headingClass}>9. Minor Athletes and COPPA Compliance</h2>
          <div className={cardClass}>
            <p className={bodyClass}>
              Account holders must be 18 or older. Athlete profiles frequently involve minors. By creating an athlete profile for a minor, you represent that you are that minor's parent or legal guardian and consent on their behalf to the collection and use of their information as described in our Privacy Policy.
            </p>
            <p className={bodyClass}>
              You are responsible for ensuring that any content you upload involving minors complies with applicable laws, including obtaining any necessary consents from other parents or guardians whose children appear in your media.
            </p>
          </div>
        </div>

        <div className={sectionClass}>
          <h2 className={headingClass}>10. The Loop</h2>
          <div className={cardClass}>
            <p className={bodyClass}>
              When you invite someone to The Loop, you are granting them access to the content you've specified at the access level you've assigned. You are responsible for managing who has access to your account and for removing access when appropriate. LegacyLoop is not responsible for how invited members of The Loop use content they have been authorized to view.
            </p>
          </div>
        </div>

        <div className={sectionClass}>
          <h2 className={headingClass}>11. Recruiting Features</h2>
          <div className={cardClass}>
            <p className={bodyClass}>
              The Recruiting Center provides informational tools including NCAA/NAIA calendar data, an AI-powered email drafting tool, and a college interest tracker. These are planning tools intended to help families organize their recruiting process.
            </p>
            <p className={bodyClass}>
              LegacyLoop does not guarantee the accuracy or completeness of NCAA/NAIA calendar data. Recruiting rules change frequently; always verify current rules directly with the NCAA, NAIA, or your athletic association. LegacyLoop is not responsible for any recruiting decisions made based on information provided by the platform.
            </p>
            <p className={bodyClass}>
              AI-generated coach emails are drafts for your review. You are responsible for the final content of any communication you send to coaches or institutions.
            </p>
          </div>
        </div>

        <div className={sectionClass}>
          <h2 className={headingClass}>12. AI-Generated Content</h2>
          <div className={cardClass}>
            <p className={bodyClass}>
              LegacyLoop uses Anthropic Claude to generate journal prompts and coach email drafts. AI-generated content is provided as a starting point and may not always be accurate, appropriate, or complete. You are responsible for reviewing and editing any AI-generated content before using it.
            </p>
          </div>
        </div>

        <div className={sectionClass}>
          <h2 className={headingClass}>13. Prohibited Uses</h2>
          <div className={cardClass}>
            <p className={bodyClass}>You may not use LegacyLoop to:</p>
            <ul className={listClass}>
              <li>Upload content that is illegal, obscene, defamatory, or violates any third party's rights</li>
              <li>Upload content depicting the exploitation or abuse of minors</li>
              <li>Impersonate any person or entity</li>
              <li>Attempt to gain unauthorized access to other accounts or our systems</li>
              <li>Use the service for any commercial purpose without our written consent</li>
              <li>Scrape, crawl, or extract data from the platform by automated means</li>
              <li>Transmit malware, viruses, or any code designed to harm or disrupt systems</li>
              <li>Circumvent or attempt to circumvent any storage limits or plan restrictions</li>
            </ul>
            <p className={bodyClass}>
              To help enforce these restrictions, uploaded images are automatically scanned using AWS Rekognition, an AI-powered content moderation service. Images flagged as containing prohibited content may be automatically rejected before they are saved to your account. If you believe an image was incorrectly flagged, contact us at support@mylegacyloop.com and we will review it manually. LegacyLoop is not liable for content that is incorrectly identified as prohibited by this automated system.
            </p>
          </div>
        </div>

        <div className={sectionClass}>
          <h2 className={headingClass}>14. Service Modifications and Availability</h2>
          <div className={cardClass}>
            <p className={bodyClass}>
              We reserve the right to modify, suspend, or discontinue any part of the service at any time. For paid subscribers, we will make reasonable efforts to provide advance notice of material changes. We do not guarantee uninterrupted availability and are not liable for downtime caused by maintenance, outages, or circumstances beyond our control.
            </p>
          </div>
        </div>

        <div className={sectionClass}>
          <h2 className={headingClass}>15. Disclaimer of Warranties</h2>
          <div className={cardClass}>
            <p className={bodyClass}>
              LegacyLoop is provided "as is" and "as available" without warranties of any kind, either express or implied, including but not limited to implied warranties of merchantability, fitness for a particular purpose, or non-infringement. We do not warrant that the service will be error-free, uninterrupted, or free of viruses or other harmful components.
            </p>
          </div>
        </div>

        <div className={sectionClass}>
          <h2 className={headingClass}>16. Limitation of Liability</h2>
          <div className={cardClass}>
            <p className={bodyClass}>
              To the fullest extent permitted by law, LegacyLoop and its owners, employees, and affiliates shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of data, loss of profits, or loss of goodwill, arising from your use of or inability to use the service.
            </p>
            <p className={bodyClass}>
              Our total liability to you for any claim arising out of or relating to these Terms or the service shall not exceed the amount you paid us in the 12 months preceding the claim.
            </p>
          </div>
        </div>

        <div className={sectionClass}>
          <h2 className={headingClass}>17. Termination</h2>
          <div className={cardClass}>
            <p className={bodyClass}>
              We may suspend or terminate your account immediately, without prior notice, if we believe you have violated these Terms, engaged in fraudulent activity, or posed a risk to the platform or other users. Upon termination, your right to use the service ceases immediately.
            </p>
            <p className={bodyClass}>
              You may terminate your account at any time by canceling your subscription and contacting us to request deletion. See Section 6 for data retention details.
            </p>
          </div>
        </div>

        <div className={sectionClass}>
          <h2 className={headingClass}>18. Governing Law</h2>
          <div className={cardClass}>
            <p className={bodyClass}>
              These Terms are governed by the laws of the State of Arizona, United States, without regard to its conflict of law provisions. Any disputes arising under these Terms shall be resolved in the courts of Maricopa County, Arizona, and you consent to personal jurisdiction in those courts.
            </p>
          </div>
        </div>

        <div className={sectionClass}>
          <h2 className={headingClass}>19. Changes to These Terms</h2>
          <div className={cardClass}>
            <p className={bodyClass}>
              We may update these Terms from time to time. When we make material changes, we will update the effective date at the top of this page and notify you by email where required. Your continued use of the service after updated Terms take effect constitutes your acceptance.
            </p>
          </div>
        </div>

        <div className={sectionClass}>
          <h2 className={headingClass}>20. Contact</h2>
          <div className={cardClass}>
            <p className={bodyClass}>
              Questions about these Terms? Contact us:
            </p>
            <p className={bodyClass}>
              <strong className="text-white">Email:</strong> support@mylegacyloop.com<br />
              <strong className="text-white">Website:</strong> mylegacyloop.com/contact
            </p>
          </div>
        </div>

      </div>
    </div>
  );
}
